// Sync: the local universe mirrors to the cloud when signed in.
// Push dirty records, pull newer ones, latest-wins; the op-log and the
// Time Machine make latest-wins safe (docs/ARCHITECTURE.md).

import { api } from "@/lib/api";
import { track } from "@/analytics/service";
import { db } from "./db";
import type { Obj } from "./types";

const CURSOR_KEY = "sync.cursor";
const PUSHED_KEY = "sync.pushedAt";
let timer: ReturnType<typeof setInterval> | null = null;
let running = false;
let kick: ReturnType<typeof setTimeout> | null = null;

/** Debounced nudge after local changes (sharing needs the cloud current). */
export function requestSync(): void {
  if (!timer) return; // not signed in
  if (kick) clearTimeout(kick);
  kick = setTimeout(() => void syncNow(), 1500);
}

export function startSync(): void {
  if (timer) return;
  void syncNow();
  timer = setInterval(() => void syncNow(), 30_000);
  window.addEventListener("online", onOnline);
}

export function stopSync(): void {
  if (timer) clearInterval(timer);
  timer = null;
  window.removeEventListener("online", onOnline);
}

function onOnline(): void {
  void syncNow();
}

export async function syncNow(): Promise<void> {
  if (running || !navigator.onLine) return;
  running = true;
  try {
    await push();
    await pull();
  } catch {
    // Offline or signed out: everything still lives locally, by design.
  } finally {
    running = false;
  }
}

async function push(): Promise<void> {
  const pushedAt = ((await db.kv.get(PUSHED_KEY))?.value as number) ?? 0;
  const now = Date.now();

  const objects = await db.objects.where("updatedAt").above(pushedAt).toArray();
  for (let i = 0; i < objects.length; i += 200) {
    await api("/api/graph/objects", { method: "PUT", json: { objects: objects.slice(i, i + 200) } });
  }

  const edges = await db.edges.where("at").above(pushedAt).toArray();
  if (edges.length > 0) await api("/api/graph/edges", { method: "PUT", json: { edges } });

  const batches = await db.ops.where("at").above(pushedAt).toArray();
  const byDraft = new Map<string, typeof batches>();
  for (const b of batches) {
    const arr = byDraft.get(b.draftId) ?? [];
    arr.push(b);
    byDraft.set(b.draftId, arr);
  }
  for (const [draftId, list] of byDraft) {
    for (let i = 0; i < list.length; i += 150) {
      await api(`/api/graph/ops/${draftId}`, { method: "POST", json: { batches: list.slice(i, i + 150) } });
    }
  }

  const dna = await db.dna.where("updatedAt").above(pushedAt).toArray();
  if (dna.length > 0) {
    await api("/api/graph/dna", {
      method: "PUT",
      json: { records: dna.map((d) => ({ objectId: d.objectId, profile: d, updatedAt: d.updatedAt })) },
    });
  }

  await db.kv.put({ key: PUSHED_KEY, value: now });
}

async function pull(): Promise<void> {
  const since = ((await db.kv.get(CURSOR_KEY))?.value as number) ?? 0;
  const { objects } = await api<{ objects: Obj[] }>(`/api/graph/objects?since=${since}&limit=200`);
  let conflicts = 0;
  for (const remote of objects) {
    const local = await db.objects.get(remote.id);
    if (!local || local.updatedAt < remote.updatedAt) {
      if (local && local.updatedAt !== remote.updatedAt) conflicts += 1;
      await db.objects.put(remote);
    }
  }
  if (conflicts > 0) track("sync.conflict", {});
  const maxUpdated = objects.reduce((m, o) => Math.max(m, o.updatedAt), since);
  await db.kv.put({ key: CURSOR_KEY, value: maxUpdated });
}
